import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSiteContent, type SiteEntry } from "@/lib/content.functions";
import { DEFAULT_CONTENT, type Entry, type TField } from "@/content/defaults";
import { useLang } from "@/lib/i18n";
import type { RealtimeChannel } from "@supabase/supabase-js";

export type Lang = "EN" | "FI";

export interface MergedEntry {
  key: string;
  order: number;
  data: Record<string, unknown>;
  image: string | null;
}

type SectionMap = Record<string, MergedEntry[]>; // keyed by `${page}/${section}`

function isTField(v: unknown): v is TField {
  return !!v && typeof v === "object" && "en" in (v as object) && "fi" in (v as object);
}

/** Resolve a field value to a string for the active language. */
export function pick(value: unknown, lang: Lang): string {
  if (value == null) return "";
  if (isTField(value)) return (lang === "FI" ? value.fi : value.en) || value.en || "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
}

function buildMap(defaults: Entry[], db: SiteEntry[]): SectionMap {
  const map = new Map<string, Map<string, MergedEntry>>();

  const ensure = (page: string, section: string) => {
    const k = `${page}/${section}`;
    if (!map.has(k)) map.set(k, new Map());
    return map.get(k)!;
  };

  for (const e of defaults) {
    ensure(e.page, e.section).set(e.key, {
      key: e.key,
      order: e.order,
      data: { ...e.data },
      image: e.image ?? null,
    });
  }

  for (const r of db) {
    const bucket = ensure(r.page, r.section);
    const existing = bucket.get(r.item_key);
    bucket.set(r.item_key, {
      key: r.item_key,
      order: r.sort_order,
      data: { ...(existing?.data ?? {}), ...(r.data ?? {}) },
      image: r.image_url ?? existing?.image ?? null,
    });
  }

  const out: SectionMap = {};
  for (const [k, bucket] of map) {
    out[k] = Array.from(bucket.values()).sort((a, b) => a.order - b.order);
  }
  return out;
}

interface ContentCtx {
  lang: Lang;
  map: SectionMap;
}

const Ctx = createContext<ContentCtx>({ lang: "EN", map: {} });

export function ContentProvider({ children }: { children: ReactNode }) {
  const { lang } = useLang();
  const queryClient = useQueryClient();
  const { data: db } = useQuery({
    queryKey: ["site-content"],
    queryFn: () => getSiteContent(),
    staleTime: 60_000,
  });

  useEffect(() => {
    // Only subscribe to live content updates for signed-in users (admins editing content).
    // Public visitors don't need realtime — it wastes a websocket and surfaces DNS/CSP
    // errors in the console on networks that block Supabase.
    let channel: RealtimeChannel | null = null;
    let cancelled = false;
    let cleanupClient: typeof import("@/integrations/supabase/client").supabase | null = null;

    void import("@/integrations/supabase/client").then(async ({ supabase }) => {
      if (cancelled) return;
      const { data } = await supabase.auth.getSession();
      if (cancelled || !data.session) return;
      cleanupClient = supabase;
      channel = supabase
        .channel("content-entries-live", { config: { private: true } })
        .on(
          "broadcast",
          { event: "content_changed" },
          () => {
            queryClient.invalidateQueries({ queryKey: ["site-content"] });
          },
        )
        .subscribe();
    });

    return () => {
      cancelled = true;
      if (channel && cleanupClient) cleanupClient.removeChannel(channel);
    };
  }, [queryClient]);


  const map = useMemo(() => buildMap(DEFAULT_CONTENT, db ?? []), [db]);

  return <Ctx.Provider value={{ lang, map }}>{children}</Ctx.Provider>;
}

/** All entries for a page/section, ordered. */
export function useEntries(page: string, section: string): MergedEntry[] {
  const { map } = useContext(Ctx);
  return map[`${page}/${section}`] ?? [];
}

/** A single entry (default key "main"). */
export function useEntry(page: string, section: string, key = "main"): MergedEntry | undefined {
  const list = useEntries(page, section);
  return list.find((e) => e.key === key) ?? list[0];
}

/** Active language. */
export function useContentLang(): Lang {
  return useContext(Ctx).lang;
}

/** Convenience: read+localize a field from an entry's data. */
export function useField() {
  const lang = useContentLang();
  return (entry: MergedEntry | undefined, field: string, fallback = ""): string => {
    if (!entry) return fallback;
    const v = entry.data[field];
    const s = pick(v, lang);
    return s || fallback;
  };
}
